"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebRequestWorkerSingleton = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const axios_1 = require("axios");
const https_proxy_agent_1 = require("https-proxy-agent");
process.env.VSCODE_DOTNET_INSTALL_TOOL_ORIGINAL_HOME = process.env.HOME;
// IMPORTING THIS LIBRARY SETS 'HOME' VARIABLE
// Causing Git to BREAK!
const get_proxy_settings_1 = require("get-proxy-settings");
// NODE JS CASTS UNDEFINED ENV VAR TO STRING 'undefined'
if (process.env.VSCODE_DOTNET_INSTALL_TOOL_ORIGINAL_HOME === 'undefined') {
    delete process.env.HOME;
}
else {
    process.env.HOME = process.env.VSCODE_DOTNET_INSTALL_TOOL_ORIGINAL_HOME;
}
const axios_cache_interceptor_1 = require("axios-cache-interceptor");
const axiosRetry = require("axios-retry");
const dns = require("dns");
const fs = require("fs");
const util_1 = require("util");
const stream = require("stream");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const FileUtilities_1 = require("./FileUtilities");
const InstallIdUtilities_1 = require("./InstallIdUtilities");
class WebRequestWorkerSingleton {
    constructor() {
        try {
            const uncachedAxiosClient = axios_1.default.create();
            // Wrap the client with a retry interceptor. We don't need to return a new client, it should be applied automatically.
            axiosRetry(uncachedAxiosClient, {
                // Inject a custom retry delay to exponentially increase the time until we retry.
                retryDelay(retryCount) {
                    return Math.pow(2, retryCount); // Takes in the int as (ms) to delay.
                }
            });
            // Record when the web requests begin:
            // Register this so it happens before the cache https://axios-cache-interceptor.js.org/guide/interceptors#explanation
            uncachedAxiosClient.interceptors.request.use(config => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                config.startTime = process.hrtime.bigint();
                return config;
            });
            // Record when the server responds:
            // Register this so it happens after the cache -- this means we need to check if the result is cached before reporting perf data!
            // ^ the request should not be processed if it is in the cache, it seems nonsensical to check this before the cache is hit even though this is possible
            uncachedAxiosClient.interceptors.response.use(res => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                res.startTime = res.config.startTime;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                res.finalTime = process.hrtime.bigint();
                return res;
            }, res => // triggered when status code is not 2XX
             {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                res.startTime = res.config.startTime;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                res.finalTime = process.hrtime.bigint();
                return res;
            });
            this.client = (0, axios_cache_interceptor_1.setupCache)(uncachedAxiosClient, {
                storage: (0, axios_cache_interceptor_1.buildMemoryStorage)(),
                ttl: 120000 // 2 Minute TTL
            });
        }
        catch (error) // We don't trust the interceptors / client to not break one another. This will cause a total failure and is not eventstream trackable,
         
        // Since this is a singleton. Propogate this error so we can report it later when we use the native web mechanism.
        {
            this.clientCreationError = error;
            this.client = null;
        }
    }
    static getInstance() {
        if (!WebRequestWorkerSingleton.instance) {
            WebRequestWorkerSingleton.instance = new WebRequestWorkerSingleton();
        }
        return WebRequestWorkerSingleton.instance;
    }
    // In the event we want to do this later, keep this for now. ATM it does nothing.
    destroy() {
    }
    /**
     *
     * @param url The URL of the website to send a get request to.
     * @param options The AXIOS flavor options dictionary which will be forwarded to an axios call.
     * @returns The response from AXIOS. The response may be in ANY type, string by default, but maybe even JSON ...
     * depending on whatever the request return content can be casted to.
     * @remarks This function is used as a custom axios.get with a timeout because axios does not correctly handle CONNECTION-based timeouts:
     * https://github.com/axios/axios/issues/647 (e.g. bad URL/site down).
     */
    getWithAxiosOrFetch(url_1, ctx_1) {
        return __awaiter(this, arguments, void 0, function* (url, ctx, options = {}, useFetchDownload = false) {
            if (url === '' || !url) {
                throw new EventStreamEvents_1.EventBasedError('AxiosGetFailedWithInvalidURL', `Request to the url ${url} failed, as the URL is invalid.`);
            }
            const timeoutCancelTokenHook = new AbortController();
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            const timeout = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                timeoutCancelTokenHook.abort();
                ctx.eventStream.post(new EventStreamEvents_1.WebRequestTime(`Timer for request:`, String(this.timeoutMsFromCtx(ctx)), 'false', url, '777')); // 777 for custom abort status. arbitrary
                if (!(yield this.isOnline(ctx.timeoutSeconds, ctx.eventStream))) {
                    const offlineError = new EventStreamEvents_1.EventBasedError('DotnetOfflineFailure', 'No internet connection detected: Cannot install .NET');
                    ctx.eventStream.post(new EventStreamEvents_1.DotnetOfflineFailure(offlineError, null));
                    throw offlineError;
                }
                const formattedError = new Error(`TIMEOUT: The request to ${url} timed out at ${ctx.timeoutSeconds} s. This only occurs if your internet
 or the url are experiencing connection difficulties; not if the server is being slow to respond. Check your connection, the url, and or increase the timeout value here: https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/troubleshooting-runtime.md#install-script-timeouts`);
                ctx.eventStream.post(new EventStreamEvents_1.WebRequestError(new EventStreamEvents_1.EventBasedError('WebRequestError', formattedError.message, formattedError.stack), null));
                throw formattedError;
            }), this.timeoutMsFromCtx(ctx));
            // Make the web request
            let response;
            if (this.client !== null) {
                response = yield this.client.get(url, Object.assign({ signal: timeoutCancelTokenHook.signal }, options));
                // Timeout for Web Request -> Not Timer. (Don't want to introduce more CPU time into timer)
                clearTimeout(timeout);
                this.reportTimeAnalytics(response, options, url, ctx);
            }
            else {
                response = yield this.getFetchResponse(url, ctx, useFetchDownload);
                clearTimeout(timeout);
            }
            // Response
            return response;
        });
    }
    getFetchResponse(url_1, ctx_1) {
        return __awaiter(this, arguments, void 0, function* (url, ctx, returnDownloadStream = false) {
            ctx.eventStream.post(new EventStreamEvents_1.WebRequestUsingAltClient(url, `Using fetch over axios, as axios failed. Axios failure: ${this.clientCreationError ? JSON.stringify(this.clientCreationError) : ''}`));
            try {
                const response = yield fetch(url, { signal: AbortSignal.timeout(ctx.timeoutSeconds * 1000) });
                if (url.includes('json')) {
                    const responseJson = yield response.json();
                    return Object.assign({ data: responseJson }, response);
                }
                else {
                    if (returnDownloadStream && (response === null || response === void 0 ? void 0 : response.body)) {
                        // Wrap it in the same data type interface as axios for piping
                        return Object.assign({ data: stream.Readable.fromWeb(response.body) }, response);
                    }
                    const responseText = yield response.text();
                    return Object.assign({ data: responseText }, response);
                }
            }
            catch (error) {
                ctx.eventStream.post(new EventStreamEvents_1.WebRequestError(error, (0, InstallIdUtilities_1.getInstallFromContext)(ctx)));
                return null;
            }
        });
    }
    /**
     * @returns The data from a web request that was hopefully cached. Even if it wasn't cached, we will make an attempt to get the data.
     * @remarks This function is no longer needed as the data is cached either way if you call makeWebRequest, but it was kept to prevent breaking APIs.
     */
    getCachedData(url_1, ctx_1) {
        return __awaiter(this, arguments, void 0, function* (url, ctx, retriesCount = 2) {
            return this.makeWebRequest(url, ctx, true, retriesCount);
        });
    }
    reportTimeAnalytics(response, options, url, ctx, manualFinalTime = null) {
        // Streamed responses return out bits of data to be piped, so this would record the end time as if only the first few bytes finished.
        // Instead we can manually report this when the stream is finished.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!manualFinalTime && (options === null || options === void 0 ? void 0 : options.responseType) === 'stream') {
            return;
        }
        // Standard timeout time in NS : 60,000,000,000 is < than std max_safe_int_size: 9,007,199,254,740,991
        const timerPrecision = 2; // decimal places for timer result
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const startTimeNs = response === null || response === void 0 ? void 0 : response.startTime;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const finalTimeNs = manualFinalTime !== null && manualFinalTime !== void 0 ? manualFinalTime : response === null || response === void 0 ? void 0 : response.finalTime;
        let durationMs = '-1';
        if (startTimeNs && finalTimeNs && finalTimeNs - startTimeNs < Number.MAX_SAFE_INTEGER) {
            durationMs = (Number(finalTimeNs - startTimeNs) / 1000000).toFixed(timerPrecision);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ctx.eventStream.post(new EventStreamEvents_1.WebRequestTimeUnknown(`Timer for request failed. Start time: ${startTimeNs}, end time: ${finalTimeNs}`, durationMs, 'true', url, String(response === null || response === void 0 ? void 0 : response.status)));
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!(response === null || response === void 0 ? void 0 : response.cached)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ctx.eventStream.post(new EventStreamEvents_1.WebRequestTime(`Timer for request:`, durationMs, 'true', url, String(response === null || response === void 0 ? void 0 : response.status)));
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ctx.eventStream.post(new EventStreamEvents_1.WebRequestCachedTime(`Cached Timer for request:`, durationMs, 'true', url, String(response === null || response === void 0 ? void 0 : response.status)));
        }
    }
    isOnline(timeoutSec, eventStream) {
        return __awaiter(this, void 0, void 0, function* () {
            const microsoftServerHostName = 'www.microsoft.com';
            const expectedDNSResolutionTimeMs = Math.max(timeoutSec * 10 * 2, 100); // Assumption: DNS resolution should take less than 1/50th of the time it'd take to download .NET.
            // ... 100 ms is there as a default to prevent the dns resolver from throwing a runtime error if the user sets timeoutSeconds to 0.
            const dnsResolver = new dns.promises.Resolver({ timeout: expectedDNSResolutionTimeMs });
            const couldConnect = yield dnsResolver.resolve(microsoftServerHostName).then(() => {
                return true;
            }).catch((error) => {
                eventStream.post(new EventStreamEvents_1.OfflineDetectionLogicTriggered(error, `DNS resolution failed at microsoft.com, ${JSON.stringify(error)}.`));
                return false;
            });
            return couldConnect;
        });
    }
    /**
     *
     * @param urlInQuestion
     * @returns true if the url was in the cache before this function executes, false else.
     *
     * @remarks Calling this WILL put the url data in the cache as we need to poke the cache to properly get the information.
     * (Checking the storage cache state results in invalid results.)
     * Returns false if the url is unavailable.
     */
    isUrlCached(urlInQuestion, ctx) {
        return __awaiter(this, void 0, void 0, function* () {
            if (urlInQuestion === '' || !urlInQuestion) {
                return false;
            }
            try {
                const response = yield this.getWithAxiosOrFetch(urlInQuestion, ctx, yield this.getAxiosOptions(ctx, 3));
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const cachedState = response === null || response === void 0 ? void 0 : response.cached;
                return cachedState !== null && cachedState !== void 0 ? cachedState : false;
            }
            catch (error) // The url was unavailable.
             {
                return false;
            }
        });
    }
    GetProxyAgentIfNeeded(ctx) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let discoveredProxy = '';
                if (!this.proxySettingConfiguredManually(ctx)) {
                    const autoDetectProxies = yield (0, get_proxy_settings_1.getProxySettings)();
                    if (autoDetectProxies === null || autoDetectProxies === void 0 ? void 0 : autoDetectProxies.https) {
                        discoveredProxy = autoDetectProxies.https.toString();
                    }
                    else if (autoDetectProxies === null || autoDetectProxies === void 0 ? void 0 : autoDetectProxies.http) {
                        discoveredProxy = autoDetectProxies.http.toString();
                    }
                }
                if (this.proxySettingConfiguredManually(ctx) || discoveredProxy) {
                    const finalProxy = (ctx === null || ctx === void 0 ? void 0 : ctx.proxyUrl) && (ctx === null || ctx === void 0 ? void 0 : ctx.proxyUrl) !== '""' && (ctx === null || ctx === void 0 ? void 0 : ctx.proxyUrl) !== '' ? ctx.proxyUrl : discoveredProxy;
                    ctx.eventStream.post(new EventStreamEvents_1.ProxyUsed(`Utilizing the Proxy : Manual ? ${ctx === null || ctx === void 0 ? void 0 : ctx.proxyUrl}, Automatic: ${discoveredProxy}, Decision : ${finalProxy}`));
                    const proxyAgent = new https_proxy_agent_1.HttpsProxyAgent(finalProxy);
                    return proxyAgent;
                }
            }
            catch (error) {
                ctx.eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(error, `The proxy lookup failed, most likely due to limited registry access. Skipping automatic proxy lookup.`));
            }
            return null;
        });
    }
    /**
     * @returns an empty promise. It will download the file from the url. The url is expected to be a file server that responds with the file directly.
     * We cannot use a simpler download pattern because we need to download the byte stream 1-1.
     */
    downloadFile(url, dest, ctx) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (yield new FileUtilities_1.FileUtilities().exists(dest)) {
                return;
            }
            const file = fs.createWriteStream(dest, { flags: 'wx' });
            // Axios Cache Interceptor Does Not Work with Stream Response Types
            const options = yield this.getAxiosOptions(ctx, 3, { responseType: 'stream', cache: false }, false);
            try {
                yield this.getWithAxiosOrFetch(url, ctx, options, true)
                    .then((response) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.pipe(file);
                    yield (0, util_1.promisify)(stream.finished)(file)
                        .then(() => {
                        this.reportTimeAnalytics(response, {}, url, ctx, process.hrtime.bigint());
                    }, () => {
                        this.reportTimeAnalytics(response, {}, url, ctx, process.hrtime.bigint());
                    });
                    return;
                }));
            }
            catch (error) {
                // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if ((error === null || error === void 0 ? void 0 : error.message) && ((_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes('ENOSPC'))) {
                    const err = new EventStreamEvents_1.DiskIsFullError(new EventStreamEvents_1.EventBasedError('DiskIsFullError', `You don't have enough space left on your disk to install the .NET SDK. Please clean up some space.`), (0, InstallIdUtilities_1.getInstallFromContext)(ctx));
                    ctx.eventStream.post(err);
                    throw err.error;
                }
                else {
                    const err = new EventStreamEvents_1.DotnetDownloadFailure(new EventStreamEvents_1.EventBasedError('DotnetDownloadFailure', 
                    // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    `We failed to download the .NET Installer. Please try to install the .NET SDK manually. Error: ${error === null || error === void 0 ? void 0 : error.message}`), (0, InstallIdUtilities_1.getInstallFromContext)(ctx));
                    ctx.eventStream.post(err);
                    throw err.error;
                }
            }
        });
    }
    getAxiosOptions(ctx_1, numRetries_1, furtherOptions_1) {
        return __awaiter(this, arguments, void 0, function* (ctx, numRetries, furtherOptions, keepAlive = true) {
            const proxyAgent = yield this.GetProxyAgentIfNeeded(ctx);
            const options = Object.assign(Object.assign(Object.assign(Object.assign({ timeout: this.timeoutMsFromCtx(ctx), 'axios-retry': { retries: numRetries } }, (keepAlive && { headers: { 'Connection': 'keep-alive' } })), (proxyAgent !== null && { proxy: false })), (proxyAgent !== null && { httpsAgent: proxyAgent })), furtherOptions);
            return options;
        });
    }
    /**
     *
     * @param throwOnError Should we throw if the connection fails, there's a bad URL passed in, or something else goes wrong?
     * @param numRetries The number of retry attempts if the url is not giving a good response.
     * @returns The data returned from a get request to the url. It may be of string type, but it may also be of another type if the return result is convert-able (e.g. JSON.)
     * @remarks protected for ease of testing.
     */
    makeWebRequest(url, ctx, throwOnError, numRetries) {
        return __awaiter(this, void 0, void 0, function* () {
            ctx.eventStream.post(new EventStreamEvents_1.WebRequestInitiated(`Making Web Request For ${url}`));
            const options = yield this.getAxiosOptions(ctx, numRetries);
            try {
                ctx.eventStream.post(new EventStreamEvents_1.WebRequestSent(url));
                const response = yield this.getWithAxiosOrFetch(url, ctx, Object.assign({}, options));
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                return response === null || response === void 0 ? void 0 : response.data;
            }
            catch (error) {
                const altResponse = yield this.getFetchResponse(url, ctx);
                if (altResponse !== null) {
                    return altResponse;
                }
                if (throwOnError) {
                    if ((0, axios_1.isAxiosError)(error)) {
                        const axiosBasedError = error;
                        // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        const summarizedError = new EventStreamEvents_1.EventBasedError('WebRequestFailedFromAxios', `Request to ${url} Failed: ${axiosBasedError === null || axiosBasedError === void 0 ? void 0 : axiosBasedError.message}. Aborting.
${axiosBasedError.cause ? `Error Cause: ${axiosBasedError.cause.message}` : ``}
Please ensure that you are online.

If you're on a proxy and disable registry access, you must set the proxy in our extension settings. See https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/troubleshooting-runtime.md.`);
                        ctx.eventStream.post(new EventStreamEvents_1.WebRequestError(summarizedError, (0, InstallIdUtilities_1.getInstallFromContext)(ctx)));
                        throw summarizedError;
                    }
                    else {
                        // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        const genericError = new EventStreamEvents_1.EventBasedError('WebRequestFailedGenerically', `Web Request to ${url} Failed: ${error === null || error === void 0 ? void 0 : error.message}. Aborting. Stack: ${'stack' in error ? error === null || error === void 0 ? void 0 : error.stack : 'unavailable.'}`);
                        ctx.eventStream.post(new EventStreamEvents_1.WebRequestError(genericError, (0, InstallIdUtilities_1.getInstallFromContext)(ctx)));
                        throw genericError;
                    }
                }
                return undefined;
            }
        });
    }
    proxySettingConfiguredManually(ctx) {
        return (ctx === null || ctx === void 0 ? void 0 : ctx.proxyUrl) ? (ctx === null || ctx === void 0 ? void 0 : ctx.proxyUrl) !== '""' : false;
    }
    timeoutMsFromCtx(ctx) {
        return (ctx === null || ctx === void 0 ? void 0 : ctx.timeoutSeconds) * 1000;
    }
}
exports.WebRequestWorkerSingleton = WebRequestWorkerSingleton;
//# sourceMappingURL=WebRequestWorkerSingleton.js.map